import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NoSectionAccess } from '../../components/NoSectionAccess/NoSectionAccess';
import { PageTitle } from '../../ui/PageTitle/PageTitle';
import { SyncFreshnessLine } from '../../components/SyncFreshnessLine/SyncFreshnessLine';
import { HomeGreeting } from './HomeGreeting';
import { HomeRelanceSections } from './HomeRelanceSections';
import { FullResyncPrompt } from './FullResyncPrompt';
import { useFullResync } from './useFullResync';
import { SyncChecklist } from '../../components/SyncChecklist/SyncChecklist';
import { TranslationKey } from '../../i18n/translationKeys';
import { useTasksStore } from '../../stores/tasksStore';
import { useTenantStore } from '../../stores/tenantStore';
import { useRelanceAgendaEntries } from '../../hooks/useRelanceAgendaEntries';
import { useSyncStore } from '../../stores/syncStore';
import { useSessionStore } from '../../stores/session/sessionStore';
import { useAcombaBridgeStore } from '../../stores/acombaBridgeStore';
import { ExtractionProgress } from '../../components/ExtractionProgress/ExtractionProgress';
import { Banner } from '../../ui/Banner/Banner';
import { SyncType } from '../../types/sync/enums';
import { useSync } from '../../hooks/useSync';
import { useRelanceDeactivationStore } from '../../stores/relanceDeactivationStore';
import { useRefreshOnVisible } from '../../hooks/useRefreshOnVisible';
import { useRefreshOnPanelRestored } from '../../hooks/useRefreshOnPanelRestored';
import { useRefreshOnTransphereInvoicesSent } from '../../hooks/useRefreshOnTransphereInvoicesSent';
import { useRefreshOnTranspherePaymentsApplied } from '../../hooks/useRefreshOnTranspherePaymentsApplied';
import { usePartialSync } from '../../hooks/usePartialSync';
import { useFillReached } from '../../hooks/useFillReached';
import { ReliabilityIndicators } from '../../features/transphere/ReliabilityIndicators';
import { openTransphereInvoicesInPortal, openCustomerEntryInPortal, isEmbeddedInPortal } from '../../utils/portal/openInPortal';
import { transphereService } from '../../services/transphereService';
import {
    readTransphereIndicators,
    cacheTransphereUnapplied,
    cacheTransphereUnsent,
} from '../../services/transphereIndicatorCache';
import type { TransphereIndicatorDto } from '../../types/transphere/transphereIndicator';
import type { TransphereUnsentInvoicesDto } from '../../types/transphere/transphereUnsentInvoices';
import { TransphereBanner } from '../../features/transphere/TransphereBanner';
import { useCanCollectPayment } from '../../hooks/useCanCollectPayment';
import { useIsAcomba } from '../../hooks/useIsAcomba';
import { useAcombaBridgeAccessReady } from '../../hooks/useAcombaBridgeAccessReady';
import { useDesktopTransphereIndicator } from '../../hooks/useDesktopTransphereIndicator';
import { useReceivablesSummary } from '../../hooks/useReceivablesSummary';
import { ReceivablesSummary } from '../../features/kpi/ReceivablesSummary';
import { featureFlags } from '../../utils/featureFlags';
import { LinkButton } from '../../ui/LinkButton/LinkButton';
import { useSalutation } from './useSalutation';
import { useGreetingStream } from './useGreetingStream';

export const Home: React.FC = () => {
    const { t } = useTranslation();
    const dailyTasks = useTasksStore(s => s.dailyTasks);
    const fetchDailyTasks = useTasksStore(s => s.fetchDailyTasks);
    const loading = useTasksStore(s => s.loading);
    const error = useTasksStore(s => s.error);
    const fetchDeactivatedItems = useRelanceDeactivationStore(s => s.fetchDeactivatedItems);
    const fetchPaidItems = useRelanceDeactivationStore(s => s.fetchPaidItems);
    const { tenantCode, tenantSwitchedAt } = useTenantStore();
    const { syncing, progress, syncFailed, lastSyncedAt, refreshLastSyncedAt } = useSyncStore();
    const { trigger: triggerPartialSync, canSync } = usePartialSync();
    const { trigger } = useSync();
    const handleRetrySync = () => void (lastSyncedAt === null ? trigger(SyncType.Full) : triggerPartialSync());
    const phase = progress?.phase;
    const relanceAccessible = dailyTasks?.relanceAccessible ?? true;

    const extractionStatus = useAcombaBridgeStore(s => s.extractionStatus);
    const retryFailedEntity = useAcombaBridgeStore(s => s.retryFailedEntity);
    const rawExtractionFailure = extractionStatus.customers === 'failed'
        || extractionStatus.invoices === 'failed'
        || extractionStatus.payments === 'failed';
    const anyExtractionFetching = extractionStatus.customers === 'fetching'
        || extractionStatus.invoices === 'fetching'
        || extractionStatus.payments === 'fetching';
    // Sticky across a per-entity retry: retryFailedEntity flips the entity straight to 'fetching', so a
    // raw `=== 'failed'` check would drop to false mid-retry and swap this panel for the generic
    // sync-failure banner (already true for the same incomplete extraction) while the user is waiting on
    // the outcome. Cleared once nothing is failed and nothing is still fetching — either the retry
    // succeeded, or a brand-new extraction reset every entity back to 'idle'.
    const hadExtractionFailureRef = useRef(rawExtractionFailure);
    if (rawExtractionFailure) hadExtractionFailureRef.current = true;
    else if (!anyExtractionFetching) hadExtractionFailureRef.current = false;
    const hasExtractionFailure = hadExtractionFailureRef.current;

    // Figures are final once the data pull ends, so content unblocks at RelanceFill. The refresh lock and the
    // header counter read `syncing` and keep tracking the fill.
    const fillReached = useFillReached(syncing, phase);
    // TEMPORARY pilot full resync: remove with the admin full-resync endpoint.
    const fullResync = useFullResync({ syncing, trigger });
    // A failed extraction needs ExtractionProgress's own retry control, which the checklist has no
    // way to represent — never let it hide behind it. Also withheld without relance access: a user
    // who cannot see relances at all lands straight on NoSectionAccess.
    const showSyncChecklist = syncing && relanceAccessible && !fillReached && !hasExtractionFailure;
    // Independent of `syncing`: a bridge extraction failure returns before ever posting the sync, so
    // `syncing` is already back to false by the time the user can see and act on the retry — this must
    // keep ExtractionProgress on screen regardless. Rendered above the relance content (not in its place)
    // so an already-loaded list stays reachable while the bridge is down.
    const showExtractionFailure = hasExtractionFailure && relanceAccessible;
    // A generic, persistent counterpart for every other sync failure (bridge unreachable, no session,
    // HTTP error, streamed Failed/Timeout…) — see syncStore.syncFailed. Also rendered above the content,
    // and mutually exclusive with showExtractionFailure by construction (both require relanceAccessible,
    // but this one excludes hasExtractionFailure so the two never render at once).
    const showSyncFailure = syncFailed && !syncing && relanceAccessible && !hasExtractionFailure;

    const salutation = useSalutation();
    const canCollectPayment = useCanCollectPayment();
    const isAcomba = useIsAcomba();
    // See useAcombaBridgeAccessReady — /tasks/daily and the greeting must not be read before the
    // bridge's first health check has had a chance to persist the current Usager's access decision.
    const bridgeAccessReady = useAcombaBridgeAccessReady();
    const { greeting, isGreetingStreaming } = useGreetingStream(bridgeAccessReady, fillReached);
    const isPortalSession = useSessionStore(state => state.sessionKind === 'portal');
    // Acomba desktop reads the unapplied figure from the local bridge instead of the backend: it has no
    // AcceoConnect, so the cloud route cannot resolve a Transphere credential for it.
    const {
        indicator: desktopTransphereIndicator,
        showPlaceholder: showDesktopTranspherePlaceholder,
        reload: reloadDesktopTransphere,
    } = useDesktopTransphereIndicator();
    const {
        summary: receivablesSummary,
        showPlaceholder: showReceivablesPlaceholder,
        reload: reloadReceivables,
        clear: clearReceivables,
    } = useReceivablesSummary();
    const paidTabEnabled = featureFlags.paidRelancesTab;
    const wasSyncingRef = useRef(false);
    const [transphereIndicator, setTransphereIndicator] = useState<TransphereIndicatorDto | null>(null);
    const [transphereUnsent, setTransphereUnsent] = useState<TransphereUnsentInvoicesDto | null>(null);
    // One flag per check: a fast answer on one must not collapse the shared slot before the other lands.
    const [transphereIndicatorPending, setTransphereIndicatorPending] = useState(true);
    const [transphereUnsentPending, setTransphereUnsentPending] = useState(true);
    const latestUnsentRead = useRef(0);
    const latestIndicatorRead = useRef(0);

    // Advisory: a failure just hides the indicator, it never blocks the relance list.
    // dailyTasks is read via getState rather than from scope: it changes on every fetch, and depending on
    // it here would re-create refreshAll and re-run the effects that list it. Also skipped without relance
    // access: the banner's render is gated on it, so a fetch would be discarded.
    const loadTransphereIndicator = useCallback(async () => {
        // Same race as loadTransphereUnsent below: the applied-payments signal issues a short read that
        // can be overtaken by a slower one already in flight.
        const read = ++latestIndicatorRead.current;
        const tenant = useTenantStore.getState().tenantCode;
        const latestDailyTasks = useTasksStore.getState().dailyTasks;
        // A desktop session has no Connect context, so this route answers 204 there and its figure comes
        // from the bridge instead. The flag can still read true on desktop — the tenant database is shared,
        // so an earlier AcceoConnect sync leaves it set — which is why the mode, not the flag, decides.
        if (isAcomba
            || latestDailyTasks?.transphereEncaissementActive !== true
            || latestDailyTasks?.relanceAccessible === false) {
            setTransphereIndicator(null);
            setTransphereIndicatorPending(false);
            if (tenant) cacheTransphereUnapplied(tenant, null);
            return;
        }

        try {
            const indicator = await transphereService.getIndicator();
            if (read !== latestIndicatorRead.current) return;
            setTransphereIndicator(indicator);
            if (tenant) cacheTransphereUnapplied(tenant, indicator);
        } catch {
            if (read !== latestIndicatorRead.current) return;
            setTransphereIndicator(null);
        } finally {
            if (read === latestIndicatorRead.current) setTransphereIndicatorPending(false);
        }
    }, [isAcomba]);

    const loadTransphereUnsent = useCallback(async () => {
        // The send signal is a short read racing the long one every other trigger issues, so the slower
        // answer can land last and pin a count the agent has already emptied — and the cache would keep
        // serving it on the next visit.
        const read = ++latestUnsentRead.current;
        const tenant = useTenantStore.getState().tenantCode;
        const latestDailyTasks = useTasksStore.getState().dailyTasks;
        if (latestDailyTasks?.transphereEncaissementActive !== true || latestDailyTasks?.relanceAccessible === false) {
            setTransphereUnsent(null);
            setTransphereUnsentPending(false);
            if (tenant) cacheTransphereUnsent(tenant, null);
            return;
        }

        try {
            const unsent = await transphereService.getUnsentInvoices();
            if (read !== latestUnsentRead.current) return;
            setTransphereUnsent(unsent);
            if (tenant) cacheTransphereUnsent(tenant, unsent);
        } catch {
            if (read !== latestUnsentRead.current) return;
            setTransphereUnsent(null);
        } finally {
            if (read === latestUnsentRead.current) setTransphereUnsentPending(false);
        }
    }, []);

    // Isolated from refreshAll below so the bridgeAccessReady wait (see above) only holds up the piece
    // that actually depends on the relance access decision — reloadDesktopTransphere/fetchDeactivatedItems/
    // fetchPaidItems/refreshLastSyncedAt don't, and must keep loading immediately even while this settles.
    const refreshDailyTasksAndIndicators = useCallback(() => {
        if (!bridgeAccessReady) return Promise.resolve();
        // Both cloud indicators are gated by a flag daily-tasks carries, so they wait on that fetch.
        return fetchDailyTasks().then(() => Promise.all([loadTransphereIndicator(), loadTransphereUnsent()]));
    }, [bridgeAccessReady, fetchDailyTasks, loadTransphereIndicator, loadTransphereUnsent]);

    const refreshAll = useCallback(() => {
        void Promise.all([
            refreshDailyTasksAndIndicators(),
            // Rides the same triggers as the cloud indicators (page visit, tenant switch, sync completion)
            // so the two modes cannot drift apart on freshness. A no-op outside a desktop session.
            reloadDesktopTransphere(),
            reloadReceivables(),
            fetchDeactivatedItems(),
            paidTabEnabled ? fetchPaidItems() : Promise.resolve(),
            refreshLastSyncedAt(),
        ]);
    }, [refreshDailyTasksAndIndicators, fetchDeactivatedItems, fetchPaidItems, refreshLastSyncedAt, reloadDesktopTransphere, reloadReceivables, paidTabEnabled]);

    // Seed from this tenant's last answer so a revisit renders at once and revalidates in place; reserve
    // the slot only for a first cloud answer worth waiting on (desktop reads a sync-time count, never waits).
    useEffect(() => {
        const cached = tenantCode ? readTransphereIndicators(tenantCode) : undefined;
        setTransphereIndicator(cached?.unapplied ?? null);
        setTransphereUnsent(cached?.unsent ?? null);

        const awaitingFirstCloudAnswer = !cached && !isAcomba;
        setTransphereIndicatorPending(awaitingFirstCloudAnswer);
        setTransphereUnsentPending(awaitingFirstCloudAnswer);
        clearReceivables();
        // refreshAll's own bridgeAccessReady wait (see refreshDailyTasksAndIndicators above) only holds
        // up the daily-tasks/greeting piece — everything else in refreshAll must still run right away.
        // bridgeAccessReady stays in this effect's deps so the re-run once it flips actually re-triggers
        // the now-unblocked fetch, not just a stale closure over the earlier `false` value.
        refreshAll();
    }, [refreshAll, clearReceivables, tenantCode, tenantSwitchedAt, isAcomba, bridgeAccessReady]);

    // Coming back to a backgrounded panel: a colleague's sync (or the nightly job) may have landed.
    useRefreshOnVisible(refreshAll);

    // Reducing the AcombaGO side panel only hides the iframe, so neither a remount nor visibilitychange
    // happens — the host signals the restore instead.
    useRefreshOnPanelRestored(refreshAll);

    // Only the pending-send count can have moved — the relance list itself still waits on a sync.
    useRefreshOnTransphereInvoicesSent(() => { void loadTransphereUnsent(); });

    // The banner reads live, so loadTransphereIndicator settles it at once. The relance list waits on a
    // recompute, so this also starts a partial sync — the end-of-sync effect below then reloads the list,
    // dropping the customers just collected. Read syncing live so a second save mid-sync does not stack one.
    useRefreshOnTranspherePaymentsApplied(() => {
        void loadTransphereIndicator();
        if (!useSyncStore.getState().syncing && canSync) void triggerPartialSync();
    });

    // The ranked list the re-streamed greeting describes; the full refreshAll still waits for the sync's
    // terminal snapshot.
    const wasFillReachedRef = useRef(false);
    useEffect(() => {
        if (fillReached && !wasFillReachedRef.current) void refreshDailyTasksAndIndicators();
        wasFillReachedRef.current = fillReached;
    }, [fillReached, refreshDailyTasksAndIndicators]);

    useEffect(() => {
        if (wasSyncingRef.current && !syncing) refreshAll();
        wasSyncingRef.current = syncing;
    }, [syncing, refreshAll]);

    const agendaEntries = useRelanceAgendaEntries();

    const aRelancerCount = dailyTasks?.sections.reduce((sum, s) => sum + s.totalCount, 0) ?? 0;
    const aRelancerFirstLoad = loading && dailyTasks === null;
    // A failed reload keeps the list already on screen.
    const aRelancerError = error !== null && dailyTasks === null ? t(TranslationKey.ApiErrors.Generic) : null;

    // sessionKind can stay 'portal' after reopening standalone, so the live embed check is what decides
    // whether a postMessage is actually delivered — without it the banner button would silently do nothing.
    const canDeepLinkToPortal = isPortalSession && isEmbeddedInPortal();

    // Reserve the banner's slot only for a company that can actually get one. Unlike relanceAccessible
    // this fails *closed*: a tenant without Transphere would otherwise be shown a placeholder for a
    // banner it can never have.
    const showTranspherePlaceholder =
        (transphereIndicatorPending || transphereUnsentPending) && dailyTasks?.transphereEncaissementActive === true;

    // The receivables grid sits closer to the greeting than the default section gap, so the
    // spacing only tightens once the grid itself is about to render.
    const showReceivablesBlock = relanceAccessible && !isAcomba && !showSyncChecklist;

    return (
        <div className={`flex flex-col ${showReceivablesBlock ? 'gap-l' : 'gap-3xl'}`}>
            {relanceAccessible && !showSyncChecklist && (
                <div className="flex flex-col gap-m">
                    <PageTitle data-testid="home-greeting-part1">{salutation}</PageTitle>
                    <SyncFreshnessLine
                        testId="home-last-sync"
                        refreshTestId="home-last-sync-refresh"
                        onFullResyncRequest={fullResync.request}
                    />
                    {fullResync.promptOpen && <FullResyncPrompt onConfirm={fullResync.confirm} onCancel={fullResync.cancel} />}
                </div>
            )}

            {showSyncChecklist ? (
                <div className="flex flex-col gap-xl">
                    <SyncChecklist phase={phase} completed={progress?.completed ?? 0} expected={progress?.expected ?? 0} />
                </div>
            ) : (
                <div className="flex flex-col gap-xl">
                    {showExtractionFailure && (
                        <div data-testid="home-extraction-failure" className="max-w-md flex flex-col gap-s">
                            <ExtractionProgress
                                extractionStatus={extractionStatus}
                                onRetry={retryFailedEntity}
                            />
                            <LinkButton
                                size="xs"
                                data-testid="home-extraction-failure-retry-sync-btn"
                                onClick={handleRetrySync}
                                className="w-fit"
                            >
                                {t(TranslationKey.AcombaBridge.Extraction.RetrySync)}
                            </LinkButton>
                        </div>
                    )}

                    {showSyncFailure && (
                        <Banner
                            tone="danger"
                            testId="home-sync-error"
                            title={t(TranslationKey.Home.SyncFailed.Title)}
                            action={{
                                label: t(TranslationKey.Home.SyncFailed.Retry),
                                onClick: handleRetrySync,
                                testId: 'home-sync-error-retry-btn',
                            }}
                        >
                            {t(TranslationKey.Home.SyncFailed.Body)}
                        </Banner>
                    )}

                    <div className="flex flex-col gap-2xl">
                        <div className="flex flex-col gap-xl">
                            <div className="flex flex-col gap-l">
                                {relanceAccessible && !isAcomba && (
                                    <ReceivablesSummary
                                        summary={receivablesSummary}
                                        pending={showReceivablesPlaceholder}
                                    />
                                )}

                                <div className="flex max-w-dialogue flex-col gap-l">
                                    <HomeGreeting
                                        aiGreeting={greeting}
                                        relanceAccessible={relanceAccessible}
                                    />

                                    {relanceAccessible && (
                                        <ReliabilityIndicators
                                            // On desktop the unapplied figure comes from the bridge, and
                                            // canDeepLinkToPortal withholds onApplyPayments there — so the
                                            // notice drops its link and carries the Acomba navigation in the sentence.
                                            unapplied={isAcomba ? desktopTransphereIndicator : transphereIndicator}
                                            unsent={transphereUnsent}
                                            // The placeholder stands in for the whole block, so on desktop it must not
                                            // outlast a banner that is already ready: the unsent count comes from the
                                            // last sync and never waits, while the bridge read can take seconds.
                                            pending={isAcomba
                                                ? showDesktopTranspherePlaceholder && !transphereUnsent
                                                : showTranspherePlaceholder}
                                            onApplyPayments={canDeepLinkToPortal && transphereIndicator
                                                ? () => openCustomerEntryInPortal(transphereIndicator.fromDate, transphereIndicator.toDate)
                                                : undefined}
                                            onOpenUnsentInvoices={canDeepLinkToPortal ? openTransphereInvoicesInPortal : undefined}
                                        />
                                    )}
                                </div>
                            </div>

                            {relanceAccessible ? (
                                <HomeRelanceSections
                                    entries={agendaEntries}
                                    aRelancerCount={aRelancerCount}
                                    aRelancerLoading={aRelancerFirstLoad}
                                    aRelancerError={aRelancerError}
                                    onRetryARelancer={() => void fetchDailyTasks({ force: true })}
                                    waitingOnGreeting={isGreetingStreaming}
                                    canCollectPayment={canCollectPayment}
                                    paidEnabled={paidTabEnabled}
                                />
                            ) : (
                                <NoSectionAccess
                                    testId="home-no-section-access"
                                    title={t(TranslationKey.Home.NoSectionAccess.Title)}
                                    message={t(TranslationKey.Home.NoSectionAccess.Message)}
                                />
                            )}
                        </div>

                        {relanceAccessible && <TransphereBanner />}
                    </div>
                </div>
            )}
        </div>
    );
};
